import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import Skeleton from '../Skeleton';
import { Colors } from '../../styles/colors';

const PageContainer = styled.div`
  ${tw`
    container
    mx-auto
    p-4
    md:p-6
    w-full max-w-full min-w-0 box-border`}
  max-width: 1280px;

  @media (min-width: 1024px) {
    padding: 2rem 4rem;
  }
`;

const FlexContainer = styled.div`
  ${tw`
    flex
    flex-col
    lg:flex-row
    gap-6
  `}
`;

const DetailsSection = styled.div`
  ${tw`
    lg:w-2/3
    order-2
    lg:order-1
  `}
`;

const BookingSection = styled.div`
  ${tw`
    lg:w-1/3
    order-1
    lg:order-2
    p-4
    border
    rounded-lg
    shadow-card
    bg-bg-secondary
  `}
`;

// Details section components
const CarTitleSkeleton = styled(Skeleton)`
  ${tw`mb-2`}
`;

const SubtitleSkeleton = styled(Skeleton)`
  ${tw`mb-2`}
`;

const RatingSkeleton = styled.div`
  ${tw`
    flex
    items-center
    gap-2
    my-4
  `}
`;

const CarSpecsSkeleton = styled.div`
  ${tw`
    grid
    grid-cols-2
    gap-4
    my-6
  `}
`;

const SpecItemSkeleton = styled.div`
  ${tw`flex items-center gap-2`}
`;

const SectionTitleSkeleton = styled(Skeleton)`
  ${tw`mb-4`}
`;

const HostSectionSkeleton = styled.div`
  ${tw`mt-8`}
`;

const HostInfoSkeleton = styled.div`
  ${tw`flex items-center gap-4 mb-4`}
`;

const HostImageSkeleton = styled(Skeleton)`
  ${tw`w-12 h-12 rounded-full`}
`;

const HostDetailsSkeleton = styled.div`
  ${tw`flex flex-col`}
`;

const BadgeSkeleton = styled.div`
  ${tw`flex items-center gap-2`}
`;

const DescriptionSkeleton = styled.div`
  ${tw`mt-8`}
`;

const DescriptionTextSkeleton = styled.div`
  ${tw`space-y-2`}
`;

// Booking section components
const CarImageSkeleton = styled(Skeleton)`
  ${tw`
    w-full
    h-32
    md:h-40
    rounded-lg
    mb-4
  `}
`;

const PriceSkeleton = styled.div`
  ${tw`mb-2 flex items-center gap-2`}
`;

const PriceBeforeTaxesSkeleton = styled(Skeleton)`
  ${tw`mb-6`}
`;

const DropdownSkeleton = styled(Skeleton)`
  ${tw`
    w-full
    h-11
    rounded-lg
    mb-4
  `}
`;

const CheckboxSkeleton = styled.div`
  ${tw`
    flex
    items-center
    gap-2
    mb-4
  `}
`;

const DatePickerContainerSkeleton = styled.div`
  ${tw`mb-4`}
`;

const DatePickerRowSkeleton = styled.div`
  ${tw`
    flex
    flex-col
    sm:flex-row
    gap-2
    mb-4
  `}
`;

const DatePickerLabelSkeleton = styled(Skeleton)`
  ${tw`mb-2`}
`;

const DateInputSkeleton = styled(Skeleton)`
  ${tw`
    flex-1
    h-11
    rounded-lg
  `}
`;

const TimeInputSkeleton = styled(Skeleton)`
  ${tw`
    w-full
    sm:w-36
    h-11
    rounded-lg
  `}
`;

const ButtonContainerSkeleton = styled.div`
  ${tw`
    flex
    flex-row
    gap-4
    mt-8
  `}
`;

const AgeSelectSkeleton = styled(Skeleton)`
  ${tw`
    w-20
    h-11
    rounded-lg
  `}
`;

const ContinueButtonSkeleton = styled(Skeleton)`
  ${tw`
    flex-1
    h-11
    rounded-lg
  `}
  background-color: ${Colors.PrimaryRed};
`;

const ViewCarPageSkeleton: React.FC = () => {
  return (
    <PageContainer>
      <FlexContainer>
        <DetailsSection>
          {/* Car title and subtitle */}
          <CarTitleSkeleton width="70%" height="36px" className="md:h-10" />
          <SubtitleSkeleton width="30px" height="20px" />

          {/* Rating */}
          <RatingSkeleton>
            <Skeleton width="60px" height="20px" />
            <Skeleton width="80px" height="20px" />
          </RatingSkeleton>

          {/* Car specs */}
          <CarSpecsSkeleton>
            {[1, 2, 3, 4, 5].map((_, index) => (
              <SpecItemSkeleton key={index}>
                <Skeleton width="90%" height="20px" />
              </SpecItemSkeleton>
            ))}
          </CarSpecsSkeleton>

          {/* Host section */}
          <HostSectionSkeleton>
            <SectionTitleSkeleton width="120px" height="16px" />

            <HostInfoSkeleton>
              <HostImageSkeleton />
              <HostDetailsSkeleton>
                <Skeleton width="100px" height="20px" className="mb-1" />
                <Skeleton width="150px" height="16px" className="mb-1" />
                <Skeleton width="200px" height="16px" />
              </HostDetailsSkeleton>
            </HostInfoSkeleton>

            <BadgeSkeleton>
              <Skeleton width="24px" height="24px" />
              <Skeleton width="150px" height="16px" />
              <Skeleton width="80px" height="16px" />
            </BadgeSkeleton>
          </HostSectionSkeleton>

          {/* Description section */}
          <DescriptionSkeleton>
            <SectionTitleSkeleton width="120px" height="16px" />

            <DescriptionTextSkeleton>
              <Skeleton width="95%" height="16px" />
              <Skeleton width="90%" height="16px" />
              <Skeleton width="92%" height="16px" />
              <Skeleton width="85%" height="16px" />
            </DescriptionTextSkeleton>
          </DescriptionSkeleton>
        </DetailsSection>

        <BookingSection>
          {/* Car image */}
          <CarImageSkeleton />

          {/* Price */}
          <PriceSkeleton>
            <Skeleton width="60px" height="24px" />
            <Skeleton width="100px" height="24px" />
          </PriceSkeleton>
          <PriceBeforeTaxesSkeleton width="120px" height="16px" />

          {/* Pickup location */}
          <Skeleton width="120px" height="16px" className="mb-2" />
          <DropdownSkeleton />

          {/* Different return location checkbox */}
          <CheckboxSkeleton>
            <Skeleton width="20px" height="20px" />
            <Skeleton width="200px" height="16px" />
          </CheckboxSkeleton>

          {/* Date and time pickers */}
          <DatePickerContainerSkeleton>
            <DatePickerLabelSkeleton width="80px" height="16px" />

            <DatePickerRowSkeleton>
              <DateInputSkeleton />
              <TimeInputSkeleton />
            </DatePickerRowSkeleton>

            <DatePickerLabelSkeleton width="80px" height="16px" />

            <DatePickerRowSkeleton>
              <DateInputSkeleton />
              <TimeInputSkeleton />
            </DatePickerRowSkeleton>
          </DatePickerContainerSkeleton>

          {/* Age and continue button */}
          <ButtonContainerSkeleton>
            <AgeSelectSkeleton />
            <ContinueButtonSkeleton />
          </ButtonContainerSkeleton>
        </BookingSection>
      </FlexContainer>
    </PageContainer>
  );
};

export default ViewCarPageSkeleton;
